// src/components/my-journey/RankPressureWave.jsx
// Feature — Rank Pressure Wave
// Streamgraph of a manager's per-GW rank delta (places climbed/dropped),
// centred on a zero-change baseline. Displays cinematic caption cards for the
// three biggest single-GW rank swings, plus a draggable scrub handle.

import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { useLeagueData, useManagerContext } from '../../context/DataContext';

// ── Design tokens ─────────────────────────────────────────────────────
const C = {
  bg:        '#000006',
  blue:      '#3f69ae',
  turquoise: '#44d5a3',
  bad:       '#eb5757',
  good:      '#219653',
  grey:      '#8a8a8a',
  surface1:  '#1d1d1d',
  surface2:  '#272727',
  border:    '#4a4a4a',
};

// ── Chart layout constants ─────────────────────────────────────────────────────
const PAD_LEFT   = 48;
const PAD_RIGHT  = 80;
const PAD_TOP    = 28;
const PAD_BOTTOM = 28;

// ── Monotone cubic spline interpolation ────────────────────────────────────────
// Produces a smooth curve through data points using Fritsch–Carlson monotone
// cubic interpolation. Returns an SVG path "d" attribute string.
// Points: array of [x, y] pairs, all finite numbers.
const monotoneCubicSpline = (points) => {
  if (!points || points.length < 2) return '';

  const n  = points.length;
  const dx = [];
  const dy = [];
  const m  = [];
  const c1 = [];
  const c2 = [];

  // Compute deltas between consecutive points
  for (let i = 0; i < n - 1; i++) {
    dx[i] = points[i + 1][0] - points[i][0];
    dy[i] = points[i + 1][1] - points[i][1];
    m[i]  = dy[i] / dx[i];
  }

  // Initial tangents — average of neighbouring slopes
  c1[0] = m[0];
  for (let i = 1; i < n - 1; i++) {
    c1[i] = (m[i - 1] + m[i]) / 2;
  }
  c1[n - 1] = m[n - 2];

  // Monotonicity correction (Fritsch–Carlson)
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) {
      c1[i]     = 0;
      c1[i + 1] = 0;
    } else {
      const alpha = c1[i]     / m[i];
      const beta  = c1[i + 1] / m[i];
      const h     = Math.sqrt(alpha * alpha + beta * beta);
      if (h > 3) {
        c1[i]     = (3 / h) * alpha * m[i];
        c1[i + 1] = (3 / h) * beta  * m[i];
      }
    }
  }

  // Build Bézier control points
  for (let i = 0; i < n - 1; i++) {
    c2[i] = [
      points[i][0] + dx[i] / 3,
      points[i][1] + dx[i] * c1[i]     / 3,
    ];
  }

  // Assemble SVG path
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < n - 1; i++) {
    const cp1x = c2[i][0];
    const cp1y = c2[i][1];
    const cp2x = points[i + 1][0] - dx[i] / 3;
    const cp2y = points[i + 1][1] - dx[i] * c1[i + 1] / 3;
    const ex   = points[i + 1][0];
    const ey   = points[i + 1][1];
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${ex} ${ey}`;
  }

  return d;
};

// ── Format large numbers for readability (e.g. 1234567 → "1,234,567") ─────────
const formatRank = (n) =>
  typeof n === 'number' ? n.toLocaleString('en-AU') : '—';

// ── Describe a GW rank swing for caption cards ─────────────────────────────────
// Positive delta = rank worsened (higher number), negative = improved.
const swingDescription = (delta) => {
  const abs = Math.abs(delta);
  if (delta < 0) return `Rose ${formatRank(abs)} places.`;
  if (delta > 0) return `Fell ${formatRank(abs)} places.`;
  return 'No change this week.';
};


// ── Chart legend item ─────────────────────────────────────────────────────────
const LegendItem = ({ colour, label, fill, dashed, pulse, star }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: '140px', flex: '1 1 140px' }}>
    <div style={{ flexShrink: 0, width: '20px', height: '10px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {fill && (
        <div style={{ width: '20px', height: '8px', backgroundColor: colour, opacity: 0.55, borderRadius: '2px' }} />
      )}
      {dashed && (
        <div style={{ width: '20px', height: '1px', borderTop: `1px dashed ${colour}` }} />
      )}
      {pulse && (
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colour, boxShadow: `0 0 6px ${colour}` }} />
      )}
      {star && (
        <svg width="12" height="12" viewBox="0 0 14 14">
          <polygon points="7,1 8.5,5 13,5 9.5,8 11,12 7,9.5 3,12 4.5,8 1,5 5.5,5" fill={colour} />
        </svg>
      )}
    </div>
    <span style={{ fontSize: '11px', color: C.grey, lineHeight: 1.35 }}>{label}</span>
  </div>
);

// ── Empty-state card ───────────────────────────────────────────────────────────
const EmptyState = () => (
  <div
    style={{
      backgroundColor: C.surface1,
      border: `1px solid ${C.border}`,
      borderRadius: '16px',
      padding: '40px 32px',
      textAlign: 'center',
      maxWidth: '480px',
      margin: '0 auto',
    }}
  >
    <div
      style={{
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        backgroundColor: `${C.blue}20`,
        border: `2px solid ${C.blue}50`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px',
        fontSize: '24px',
      }}
    >
      📊
    </div>
    <h3
      style={{
        color: '#ffffff',
        fontSize: '16px',
        fontWeight: 700,
        marginBottom: '10px',
      }}
    >
      Rank Timeline Unavailable
    </h3>
    <p style={{ color: C.grey, fontSize: '13px', lineHeight: 1.6 }}>
      Overall rank data for this manager is not yet available. Re-run the
      latest FPL scraper to populate the{' '}
      <span style={{ color: C.turquoise, fontFamily: 'monospace' }}>
        Rank Timeline
      </span>{' '}
      sheet, then reload.
    </p>
  </div>
);

// ── Stat chip ─────────────────────────────────────────────────────────────────
const StatChip = ({ label, value, colour, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.45, ease: 'easeOut' }}
    style={{
      backgroundColor: C.surface1,
      border: `1px solid ${C.border}`,
      borderRadius: '12px',
      padding: '12px 16px',
      minWidth: '120px',
      flex: '1 1 120px',
    }}
  >
    <div
      style={{
        fontSize: '18px',
        fontWeight: 800,
        color: colour || C.turquoise,
        marginBottom: '4px',
        letterSpacing: '-0.02em',
      }}
    >
      {value}
    </div>
    <div style={{ fontSize: '11px', color: C.grey, lineHeight: 1.35 }}>
      {label}
    </div>
  </motion.div>
);

// ── Pressure pulse ring — animates infinitely on pressure events ───────────────
const PressurePulse = ({ cx, cy }) => (
  <motion.circle
    cx={cx}
    cy={cy}
    r={6}
    fill="none"
    stroke={C.bad}
    strokeWidth={1.5}
    initial={{ r: 6, opacity: 0.7 }}
    animate={{ r: 20, opacity: 0 }}
    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
  />
);

// ── Relief event star burst (⚡ rendered in SVG foreignObject) ─────────────────
const ReliefBurst = ({ cx, cy }) => (
  <motion.g
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ type: 'spring', stiffness: 300, damping: 18, delay: 2.2 }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    <circle
      cx={cx}
      cy={cy}
      r={9}
      fill={`${C.turquoise}25`}
      stroke={C.turquoise}
      strokeWidth={1}
    />
    {/* 8-point star approximated with two overlaid rotated squares */}
    <polygon
      points={`${cx},${cy - 7} ${cx + 2},${cy - 2} ${cx + 7},${cy} ${cx + 2},${cy + 2} ${cx},${cy + 7} ${cx - 2},${cy + 2} ${cx - 7},${cy} ${cx - 2},${cy - 2}`}
      fill={C.turquoise}
      opacity={0.9}
    />
  </motion.g>
);

// ── Main component ─────────────────────────────────────────────────────────────
const RankPressureWave = () => {
  const { data } = useLeagueData();
  const { selectedManager } = useManagerContext();

  // SVG dimensions — responsive via ResizeObserver on a wrapper div
  const wrapperRef   = useRef(null);
  const [svgWidth, setSvgWidth]   = useState(800);
  const svgHeight = Math.round(svgWidth * 0.30); // ~30% aspect ratio — shorter chart

  // Track whether the scrub handle is being dragged
  const [isScrubbing, setIsScrubbing] = useState(false);
  // Index of the GW under the scrub handle (0-based into managerRows)
  const [scrubIndex, setScrubIndex]   = useState(0);

  // Caption card cycling
  const [captionIndex, setCaptionIndex] = useState(0);
  const captionTimerRef = useRef(null);

  // ── Responsive SVG width ─────────────────────────────────────────────────
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setSvgWidth(Math.floor(w));
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Filter rank timeline to the selected manager ──────────────────────────
  const managerRows = useMemo(() => {
    if (!data?.rankTimeline || !selectedManager) return [];
    return [...data.rankTimeline]
      .filter((r) => r.Manager === selectedManager)
      .sort((a, b) => a.GW - b.GW);
  }, [data, selectedManager]);

  // ── Derived statistics ────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!managerRows.length) return null;

    // Only consider rows that have a defined, non-null Rank_Delta
    const rowsWithDelta = managerRows.filter(
      (r) => r.Rank_Delta !== undefined && r.Rank_Delta !== null && !isNaN(r.Rank_Delta)
    );

    if (!rowsWithDelta.length) return null;

    // Best single-GW climb = largest negative Rank_Delta (most places gained)
    const bestClimbRow = [...rowsWithDelta].sort(
      (a, b) => (a.Rank_Delta || 0) - (b.Rank_Delta || 0)
    )[0];

    // Worst single-GW drop = largest positive Rank_Delta (most places lost)
    const worstDropRow = [...rowsWithDelta].sort(
      (a, b) => (b.Rank_Delta || 0) - (a.Rank_Delta || 0)
    )[0];

    // Count GWs improved vs dropped
    const gwsImproved = rowsWithDelta.filter((r) => r.Rank_Delta < 0).length;
    const gwsDropped  = rowsWithDelta.filter((r) => r.Rank_Delta > 0).length;

    // Net season rank change: GW1 rank minus final GW rank.
    // Positive = overall improvement (climbed), negative = worsened.
    const gw1Rank   = managerRows[0].Overall_Rank;
    const finalRank = managerRows[managerRows.length - 1].Overall_Rank;
    const netChange = (typeof gw1Rank === 'number' && typeof finalRank === 'number')
      ? gw1Rank - finalRank
      : null;

    return {
      bestClimbDelta: bestClimbRow?.Rank_Delta,  // negative value (e.g. -50000)
      bestClimbGW:    bestClimbRow?.GW,
      worstDropDelta: worstDropRow?.Rank_Delta,  // positive value (e.g. 80000)
      worstDropGW:    worstDropRow?.GW,
      gwsImproved,
      gwsDropped,
      netChange,
      finalRank,
    };
  }, [managerRows]);

  // ── Delta values — chart value = -(Rank_Delta) so "climbed" = positive ───
  // Rows without a valid Rank_Delta are treated as 0 (no change) to avoid gaps.
  const deltaValues = useMemo(
    () =>
      managerRows.map((r) => {
        const raw = r.Rank_Delta;
        return raw !== undefined && raw !== null && !isNaN(raw) ? -raw : 0;
      }),
    [managerRows]
  );

  // ── Caption cards — one best climb, one worst drop, one runner-up ────────
  // Guarantees all three cards have distinct, meaningful labels.
  const captionCards = useMemo(() => {
    if (!managerRows.length) return [];
    const valid = managerRows.filter((r) => r.Rank_Delta !== undefined && r.Rank_Delta !== 0);
    if (!valid.length) return [];

    // Best single-GW climb (most negative delta = most places risen)
    const bestClimb = [...valid].sort((a, b) => a.Rank_Delta - b.Rank_Delta)[0];
    // Worst single-GW drop (most positive delta)
    const worstDrop = [...valid].sort((a, b) => b.Rank_Delta - a.Rank_Delta)[0];

    // Third: highest absolute swing from a different GW than the above two
    const usedGWs = new Set([bestClimb?.GW, worstDrop?.GW].filter(Boolean));
    const third = [...valid]
      .filter((r) => !usedGWs.has(r.GW))
      .sort((a, b) => Math.abs(b.Rank_Delta) - Math.abs(a.Rank_Delta))[0];

    const toCard = (r, label) => r
      ? { gw: r.GW, delta: r.Rank_Delta, label, desc: swingDescription(r.Rank_Delta) }
      : null;

    const thirdLabel = third
      ? (third.Rank_Delta < 0 ? 'Strong Recovery' : 'Tough Week')
      : null;

    return [
      toCard(bestClimb, 'Best Week'),
      worstDrop?.GW !== bestClimb?.GW ? toCard(worstDrop, 'Toughest Week') : null,
      third ? toCard(third, thirdLabel) : null,
    ].filter(Boolean).sort((a, b) => a.gw - b.gw);
  }, [managerRows]);

  // ── Pressure / relief event markers (capped at 3 each) ───────────────────
  // Pick highest-magnitude events (by absolute Rank_Delta)
  const pressureEvents = useMemo(
    () =>
      [...managerRows]
        .filter((r) => r.Is_Pressure_Event)
        .sort((a, b) => Math.abs(b.Rank_Delta) - Math.abs(a.Rank_Delta))
        .slice(0, 3),
    [managerRows]
  );

  const reliefEvents = useMemo(
    () =>
      [...managerRows]
        .filter((r) => r.Is_Relief_Event)
        .sort((a, b) => Math.abs(b.Rank_Delta) - Math.abs(a.Rank_Delta))
        .slice(0, 3),
    [managerRows]
  );

  // ── Auto-cycle caption cards every 4 seconds when not scrubbing ──────────
  useEffect(() => {
    if (!captionCards.length || isScrubbing) return;
    captionTimerRef.current = setInterval(() => {
      setCaptionIndex((prev) => (prev + 1) % captionCards.length);
    }, 4000);
    return () => clearInterval(captionTimerRef.current);
  }, [captionCards.length, isScrubbing]);

  // ── SVG coordinate helpers ────────────────────────────────────────────────
  const chartWidth  = svgWidth  - PAD_LEFT - PAD_RIGHT;
  const chartHeight = svgHeight - PAD_TOP  - PAD_BOTTOM;

  // Map GW number to an x pixel coordinate within the chart area
  const gwToX = useCallback(
    (gw) => {
      if (!managerRows.length) return PAD_LEFT;
      const firstGW = managerRows[0].GW;
      const lastGW  = managerRows[managerRows.length - 1].GW;
      const range   = Math.max(lastGW - firstGW, 1);
      return PAD_LEFT + ((gw - firstGW) / range) * chartWidth;
    },
    [managerRows, chartWidth]
  );

  // Map a delta value to a y pixel coordinate.
  // Positive delta = climbed = higher on screen (smaller y).
  // Y-axis spans from yMax (top) to yMin (bottom), with zero included in range.
  // Zero line sits proportionally — not forced to centre.
  const { rankToY, baselineY } = useMemo(() => {
    if (!deltaValues.length) {
      const mid = PAD_TOP + chartHeight / 2;
      return { rankToY: () => mid, baselineY: mid };
    }
    const rawMin = Math.min(...deltaValues);
    const rawMax = Math.max(...deltaValues);
    // Always include zero in the visible range
    const yMin = Math.min(rawMin, 0);
    const yMax = Math.max(rawMax, 0);
    const range = Math.max(yMax - yMin, 1);
    // Add 10% padding at top and bottom so extreme values don't hit the edges
    const padFraction = 0.10;
    const paddedMin = yMin - range * padFraction;
    const paddedMax = yMax + range * padFraction;
    const paddedRange = paddedMax - paddedMin;
    const toY = (delta) =>
      PAD_TOP + ((paddedMax - delta) / paddedRange) * chartHeight;
    return { rankToY: toY, baselineY: toY(0) };
  }, [deltaValues, chartHeight]);

  // ── Build spline point arrays ─────────────────────────────────────────────
  // rankPoints: the delta line (−Rank_Delta for each GW)
  // abovePath:  area above zero line (climbed GWs) → turquoise fill
  // belowPath:  area below zero line (dropped GWs) → red fill
  const { rankPoints, abovePath, belowPath } = useMemo(() => {
    if (!managerRows.length || !deltaValues.length) {
      return { rankPoints: [], abovePath: '', belowPath: '' };
    }

    // Map each GW's delta value to an [x, y] pixel coordinate
    const pts = managerRows.map((r, i) => [gwToX(r.GW), rankToY(deltaValues[i])]);

    // Above-baseline fill: weeks where delta > 0 (climbed) → y < baselineY
    // Clamp at baseline so fill only covers the "above zero" region
    const abovePts = pts.map(([x, y]) => [x, Math.min(y, baselineY)]);

    // Below-baseline fill: weeks where delta < 0 (dropped) → y > baselineY
    // Clamp at baseline so fill only covers the "below zero" region
    const belowPts = pts.map(([x, y]) => [x, Math.max(y, baselineY)]);

    // Build closed SVG path for the above (climbed) region
    const buildAbovePath = (topPoints) => {
      if (!topPoints.length) return '';
      const spine = monotoneCubicSpline(topPoints);
      if (!spine) return '';
      const lastX  = topPoints[topPoints.length - 1][0];
      const firstX = topPoints[0][0];
      return `${spine} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;
    };

    // Build closed SVG path for the below (dropped) region
    const buildBelowPath = (bottomPoints) => {
      if (!bottomPoints.length) return '';
      const spine = monotoneCubicSpline(bottomPoints);
      if (!spine) return '';
      const lastX  = bottomPoints[bottomPoints.length - 1][0];
      const firstX = bottomPoints[0][0];
      return `M ${firstX} ${baselineY} ${spine} L ${lastX} ${baselineY} Z`;
    };

    return {
      rankPoints: pts,
      // Turquoise fill — GWs where the manager climbed (delta positive → above zero)
      abovePath: buildAbovePath(abovePts),
      // Red fill — GWs where the manager dropped (delta negative → below zero)
      belowPath: buildBelowPath(belowPts),
    };
  }, [managerRows, deltaValues, gwToX, rankToY, baselineY]);

  // The actual rank spline (stroke only, used for animated drawing)
  const rankSplinePath = useMemo(
    () => monotoneCubicSpline(rankPoints),
    [rankPoints]
  );

  // ── Best-climb marker position — the GW with the biggest rank improvement ──
  const bestMarker = useMemo(() => {
    if (!stats || stats.bestClimbGW == null) return null;
    const idx = managerRows.findIndex((r) => r.GW === stats.bestClimbGW);
    const delta = idx >= 0 ? deltaValues[idx] : 0;
    return {
      x: gwToX(stats.bestClimbGW),
      y: rankToY(delta),
    };
  }, [stats, managerRows, deltaValues, gwToX, rankToY]);

  // ── Scrub handle drag state ───────────────────────────────────────────────
  // The drag handle moves within [0, chartWidth] relative to PAD_LEFT.
  // We map its x position to the closest GW index in managerRows.
  const handleX     = useMotionValue(0);
  const constraintRef = useRef(null); // set on the chart area div

  const handleDrag = useCallback(() => {
    const x   = handleX.get(); // offset from drag origin
    const frac = Math.max(0, Math.min(1, x / chartWidth));
    const idx  = Math.round(frac * (managerRows.length - 1));
    setScrubIndex(idx);
  }, [handleX, chartWidth, managerRows.length]);

  // Tooltip content for the scrub position
  const scrubRow = managerRows[scrubIndex] || null;

  // ── Empty / no-manager state ──────────────────────────────────────────────
  if (!managerRows.length) {
    return (
      <div
        style={{
          background: C.bg,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
        }}
      >
        <EmptyState />
      </div>
    );
  }

  // ── Dot-grid background (inline SVG radial-gradient pattern) ──────────────
  const dotBg = {
    backgroundImage:
      'radial-gradient(circle, rgba(63,105,174,0.18) 1.5px, transparent 1.5px)',
    backgroundSize: '28px 28px',
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        background: C.bg,
        ...dotBg,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        padding: '32px 24px 40px',
        boxSizing: 'border-box',
        fontFamily: "'Roboto', sans-serif",
        overflowX: 'hidden',
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
        <motion.div
          style={{ flex: 1, minWidth: 0 }}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          {stats && stats.netChange != null && (
            <div
              style={{
                fontSize: 'clamp(36px, 6vw, 64px)',
                fontWeight: 900,
                color: stats.netChange > 0 ? C.turquoise : stats.netChange < 0 ? C.bad : C.grey,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                marginBottom: '6px',
              }}
            >
              {stats.netChange > 0 ? '↑' : stats.netChange < 0 ? '↓' : '='}{' '}
              {formatRank(Math.abs(stats.netChange))}
            </div>
          )}
          <div style={{ fontSize: '13px', color: C.grey, letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: '10px' }}>
            Net global rank change across the season
          </div>
          {selectedManager && (
            <div style={{ fontSize: '13px', color: C.blue, marginTop: '4px' }}>
              {selectedManager}
            </div>
          )}
        </motion.div>

        {/* ── Rank cards (percentile + raw rank) ─────────────────────────────── */}
        {stats?.finalRank != null && (() => {
          const rank         = Number(stats.finalRank);
          const totalPlayers = Number(data?.leagueMeta?.Total_Players) || 0;
          const topPct       = totalPlayers > 0 ? Math.max(1, Math.ceil(rank / totalPlayers * 100)) : null;
          const pctColour    = topPct == null ? C.grey : topPct <= 10 ? C.turquoise : topPct <= 25 ? C.good : topPct <= 50 ? C.blue : C.grey;
          const miniCard     = (label, children) => (
            <div style={{
              backgroundColor: C.surface1, border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '12px 16px',
              textAlign: 'center', minWidth: '100px',
            }}>
              <p style={{ fontSize: '9px', fontWeight: 700, color: C.grey, textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 5px' }}>{label}</p>
              {children}
            </div>
          );
          return (
            <motion.div
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.45, delay: 0.15 }}
              style={{ display: 'flex', flexDirection: 'row', gap: '8px', flexShrink: 0 }}
            >
              {miniCard('Global percentile',
                topPct != null
                  ? <p style={{ fontSize: '20px', fontWeight: 900, color: pctColour, margin: 0, lineHeight: 1, letterSpacing: '-0.5px' }}>Top {topPct}%</p>
                  : <p style={{ fontSize: '20px', fontWeight: 900, color: C.grey, margin: 0, lineHeight: 1 }}>—</p>
              )}
              {miniCard('Final rank',
                <p style={{ fontSize: '18px', fontWeight: 900, color: 'white', margin: 0, lineHeight: 1, letterSpacing: '-0.5px' }}>
                  {rank.toLocaleString()}
                </p>
              )}
            </motion.div>
          );
        })()}
      </div>

      {/* ── Narrative explainer ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.45 }}
        style={{
          backgroundColor: 'rgba(63,105,174,0.08)',
          border: '1px solid rgba(63,105,174,0.25)',
          borderRadius: '10px',
          padding: '10px 14px',
          marginBottom: '14px',
          fontSize: '12px',
          color: C.grey,
          lineHeight: 1.6,
        }}
      >
        Each gameweek shows how many places you <span style={{ color: C.turquoise }}>climbed</span> or{' '}
        <span style={{ color: C.bad }}>dropped</span> in the global rankings. Turquoise weeks are where
        you outperformed — red weeks dragged you back. The bigger the spike, the more dramatic that week
        was for your rank.
      </motion.div>

      {/* ── Chart legend ────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 16px',
          marginBottom: '16px',
          padding: `12px 14px 12px ${PAD_LEFT}px`,
          backgroundColor: C.surface1,
          borderRadius: '10px',
          border: `1px solid ${C.border}`,
        }}
      >
        <LegendItem colour={C.turquoise} label="Weeks you climbed the table"   fill />
        <LegendItem colour={C.bad}       label="Weeks you dropped"             fill />
        <LegendItem colour={C.border}    label="No change (zero line)"         dashed />
        <LegendItem colour={C.bad}       label="Sustained pressure (3+ falls)" pulse />
        <LegendItem colour={C.turquoise} label="Big recovery jump (relief)"    star />
      </motion.div>

      {/* ── SVG streamgraph chart ────────────────────────────────────────────── */}
      <div ref={wrapperRef} style={{ width: '100%', marginBottom: '24px' }}>
        <svg
          width="100%"
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{ display: 'block', overflow: 'visible' }}
          aria-label="Rank Pressure Wave streamgraph"
        >
          {/* ── Defs: clip path to keep fills inside chart area ─────────────── */}
          <defs>
            <clipPath id="chart-clip">
              <rect
                x={PAD_LEFT}
                y={PAD_TOP}
                width={chartWidth}
                height={chartHeight}
              />
            </clipPath>
          </defs>

          {/* ── Above-baseline fill (better than average) ─────────────────── */}
          {abovePath && (
            <motion.path
              d={abovePath}
              fill={`rgba(68,213,163,0.25)`}
              stroke="none"
              clipPath="url(#chart-clip)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
          )}

          {/* ── Below-baseline fill (worse than average) ──────────────────── */}
          {belowPath && (
            <motion.path
              d={belowPath}
              fill={`rgba(235,87,87,0.2)`}
              stroke="none"
              clipPath="url(#chart-clip)"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, ease: 'easeOut' }}
            />
          )}

          {/* ── Baseline — dashed horizontal line at zero delta (no change) ── */}
          <line
            x1={PAD_LEFT}
            y1={baselineY}
            x2={PAD_LEFT + chartWidth}
            y2={baselineY}
            stroke={C.border}
            strokeWidth={1}
            strokeDasharray="4 4"
          />
          <text
            x={PAD_LEFT + chartWidth + 8}
            y={baselineY + 4}
            fill={C.grey}
            fontSize={10}
            textAnchor="start"
          >
            No change
          </text>

          {/* ── Y-axis indicator — "↑ Climbed" inside chart area, top-left ──── */}
          <text
            x={PAD_LEFT + 4}
            y={PAD_TOP + 12}
            fill={C.turquoise}
            fontSize={9}
            textAnchor="start"
            fontWeight="600"
          >
            ↑ Climbed
          </text>

          {/* ── Rank spline — animated draw from left to right ──────────────── */}
          {rankSplinePath && (
            <motion.path
              d={rankSplinePath}
              fill="none"
              stroke={C.turquoise}
              strokeWidth={1.5}
              clipPath="url(#chart-clip)"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: 'easeOut' }}
            />
          )}

          {/* Below-zero segment recoloured red for visual distinction (opacity 0 — fill handles colouring) */}
          {rankSplinePath && (
            <motion.path
              d={rankSplinePath}
              fill="none"
              stroke={C.bad}
              strokeWidth={1.5}
              clipPath="url(#chart-clip)"
              // Intentionally hidden: the red belowPath fill already colours dropped weeks.
              opacity={0}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0 }}
            />
          )}

          {/* ── Pressure event pulse rings ──────────────────────────────────── */}
          {pressureEvents.map((r) => (
            <PressurePulse
              key={`pressure-${r.GW}`}
              cx={gwToX(r.GW)}
              cy={baselineY}
            />
          ))}
          {/* Static dot marker for pressure events */}
          {pressureEvents.map((r) => (
            <circle
              key={`pressure-dot-${r.GW}`}
              cx={gwToX(r.GW)}
              cy={baselineY}
              r={3}
              fill={C.bad}
            />
          ))}

          {/* ── Relief event star bursts ────────────────────────────────────── */}
          {reliefEvents.map((r) => {
            // Resolve the delta-based Y for this relief event's GW
            const idx = managerRows.findIndex((row) => row.GW === r.GW);
            const cy  = idx >= 0 ? rankToY(deltaValues[idx]) - 14 : baselineY - 14;
            return (
              <ReliefBurst
                key={`relief-${r.GW}`}
                cx={gwToX(r.GW)}
                cy={cy}
              />
            );
          })}

          {/* ── Season-best rank marker ─────────────────────────────────────── */}
          {bestMarker && (
            <g>
              <circle
                cx={bestMarker.x}
                cy={bestMarker.y}
                r={5}
                fill={C.turquoise}
              />
              <circle
                cx={bestMarker.x}
                cy={bestMarker.y}
                r={9}
                fill="none"
                stroke={C.turquoise}
                strokeWidth={1}
                opacity={0.45}
              />
              {/* Dark background rect prevents the label from overlapping the spline */}
              <rect
                x={bestMarker.x - 14}
                y={bestMarker.y - 26}
                width={28}
                height={14}
                rx={4}
                fill="rgba(0,0,6,0.7)"
              />
              <text
                x={bestMarker.x}
                y={bestMarker.y - 17}
                fill={C.turquoise}
                fontSize={9}
                textAnchor="middle"
                dominantBaseline="central"
                fontWeight="700"
              >
                Best
              </text>
            </g>
          )}

          {/* ── X-axis GW labels (first, middle, last) ──────────────────────── */}
          {managerRows.length > 0 && (() => {
            const first  = managerRows[0];
            const last   = managerRows[managerRows.length - 1];
            const midIdx = Math.floor(managerRows.length / 2);
            const mid    = managerRows[midIdx];
            return [first, mid, last].map((r) => (
              <text
                key={`gw-label-${r.GW}`}
                x={gwToX(r.GW)}
                y={PAD_TOP + chartHeight + 18}
                fill={C.grey}
                fontSize={10}
                textAnchor="middle"
              >
                GW{r.GW}
              </text>
            ));
          })()}

          {/* ── Scrub indicator line ─────────────────────────────────────────── */}
          {isScrubbing && scrubRow && (
            <line
              x1={gwToX(scrubRow.GW)}
              y1={PAD_TOP}
              x2={gwToX(scrubRow.GW)}
              y2={PAD_TOP + chartHeight}
              stroke={C.grey}
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.7}
            />
          )}
        </svg>
      </div>

      {/* ── Scrub handle ─────────────────────────────────────────────────────── */}
      <div
        ref={constraintRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '36px',
          marginBottom: '8px',
          paddingLeft: `${PAD_LEFT}px`,
          paddingRight: `${PAD_RIGHT}px`,
          boxSizing: 'border-box',
        }}
      >
        {/* Track bar */}
        <div
          style={{
            position: 'absolute',
            left: PAD_LEFT,
            right: PAD_RIGHT,
            top: '50%',
            transform: 'translateY(-50%)',
            height: '2px',
            backgroundColor: C.border,
            borderRadius: '1px',
          }}
        />

        {/* Draggable thumb */}
        <motion.div
          drag="x"
          dragConstraints={constraintRef}
          dragElastic={0}
          style={{
            x: handleX,
            position: 'absolute',
            top: '50%',
            left: PAD_LEFT,
            translateY: '-50%',
            width: '32px',
            height: '32px',
            backgroundColor: C.surface2,
            border: `2px solid ${C.turquoise}`,
            borderRadius: '50%',
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            touchAction: 'none',
          }}
          onDragStart={() => setIsScrubbing(true)}
          onDrag={handleDrag}
          onDragEnd={() => { setIsScrubbing(false); }}
          whileDrag={{ cursor: 'grabbing', scale: 1.12 }}
          aria-label="Drag to scrub through gameweeks"
        >
          <svg width="12" height="12" viewBox="0 0 12 12">
            <path d="M3 2 L3 10 M9 2 L9 10" stroke={C.turquoise} strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </motion.div>

        {/* Scrub tooltip */}
        <AnimatePresence>
          {isScrubbing && scrubRow && (
            <motion.div
              key="scrub-tooltip"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.15 }}
              style={{
                position: 'absolute',
                left: Math.max(PAD_LEFT, Math.min(
                  PAD_LEFT + handleX.get() - 44,
                  svgWidth - PAD_RIGHT - 96
                )),
                bottom: '44px',
                backgroundColor: C.surface1,
                border: `1px solid ${C.border}`,
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '12px',
                color: '#fff',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
                zIndex: 20,
              }}
            >
              <span style={{ color: C.grey }}>GW{scrubRow.GW} · </span>
              {/* Show rank delta for the scrubbed GW: negative = climbed, positive = dropped */}
              {(() => {
                const delta = scrubRow.Rank_Delta;
                if (delta === undefined || delta === null || isNaN(delta)) {
                  return <span style={{ color: C.grey }}>No data</span>;
                }
                const absVal = formatRank(Math.abs(delta));
                if (delta < 0) {
                  return <span style={{ color: C.turquoise, fontWeight: 700 }}>↑ {absVal}</span>;
                }
                if (delta > 0) {
                  return <span style={{ color: C.bad, fontWeight: 700 }}>↓ {absVal}</span>;
                }
                return <span style={{ color: C.grey, fontWeight: 700 }}>No change</span>;
              })()}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Label */}
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '10px',
            color: C.grey,
            letterSpacing: '0.06em',
          }}
        >
          DRAG
        </div>
      </div>

      {/* ── Cinematic caption cards ──────────────────────────────────────────── */}
      {captionCards.length > 0 && (
        <div style={{ marginBottom: '28px', minHeight: '90px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={captionIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{
                backgroundColor: C.surface1,
                border: `1px solid ${
                  captionCards[captionIndex].delta < 0
                    ? `${C.turquoise}50`
                    : `${C.bad}50`
                }`,
                borderLeft: `3px solid ${
                  captionCards[captionIndex].delta < 0 ? C.turquoise : C.bad
                }`,
                borderRadius: '12px',
                padding: '16px 18px',
              }}
            >
              <div
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: C.grey,
                  marginBottom: '6px',
                }}
              >
                GW{captionCards[captionIndex].gw}
              </div>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 800,
                  color:
                    captionCards[captionIndex].delta < 0 ? C.turquoise : C.bad,
                  marginBottom: '4px',
                }}
              >
                {captionCards[captionIndex].label}
              </div>
              <div style={{ fontSize: '13px', color: '#ccc' }}>
                {captionCards[captionIndex].desc}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Caption pagination dots */}
          {captionCards.length > 1 && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '6px',
                marginTop: '10px',
              }}
            >
              {captionCards.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCaptionIndex(i)}
                  aria-label={`Caption ${i + 1}`}
                  style={{
                    width: i === captionIndex ? '18px' : '6px',
                    height: '6px',
                    borderRadius: '3px',
                    backgroundColor:
                      i === captionIndex ? C.turquoise : C.border,
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    transition: 'width 0.25s ease, background-color 0.25s ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Stats chips ──────────────────────────────────────────────────────── */}
      {stats && (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1, delayChildren: 2 } },
          }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          {/* Best single-GW climb */}
          {stats.bestClimbDelta != null && (
            <StatChip
              label={`Best single-GW climb${stats.bestClimbGW != null ? ` — GW${stats.bestClimbGW}` : ''}`}
              value={`↑ ${formatRank(Math.abs(stats.bestClimbDelta))}`}
              colour={C.turquoise}
              delay={2}
            />
          )}
          {/* Worst single-GW drop */}
          {stats.worstDropDelta != null && stats.worstDropDelta > 0 && (
            <StatChip
              label={`Worst single-GW drop${stats.worstDropGW != null ? ` — GW${stats.worstDropGW}` : ''}`}
              value={`↓ ${formatRank(Math.abs(stats.worstDropDelta))}`}
              colour={C.bad}
              delay={2.1}
            />
          )}
          {/* GWs improved */}
          <StatChip
            label="Gameweeks you climbed"
            value={stats.gwsImproved}
            colour={C.turquoise}
            delay={2.2}
          />
          {/* GWs dropped */}
          <StatChip
            label="Gameweeks you dropped"
            value={stats.gwsDropped}
            colour={C.bad}
            delay={2.3}
          />
        </motion.div>
      )}
    </div>
  );
};

export default RankPressureWave;
